import { apiConfig } from '@config/api'
import { faker } from '@faker-js/faker'
import * as Sentry from '@sentry/react'
import { type IAxiosError } from '@shared/dtos'
import { Auth } from 'aws-amplify'
import axios, {
  // AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { env } from '@/env'
import { sleep } from '@/shared/utils/sleep'

import { CacheAdapter } from '../cache'

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${apiConfig.baseUrl}`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8;',
    Accept: 'application/json; charset=utf-8',
  },
})

axiosInstance.interceptors.request.use(async (config) => {
  if (env.REACT_APP_ENABLE_DELAY) {
    await new Promise((resolve) =>
      setTimeout(resolve, faker.number.int({ min: 1000, max: 3000 })),
    )
  }

  return config
})

const AxiosInterceptor: any = ({ children }: any) => {
  useEffect(() => {
    const amplifyInterceptor = async <T = any,>(
      config: AxiosRequestConfig<T>,
    ): Promise<AxiosRequestConfig<T>> => {
      if (env.REACT_APP_ENABLE_DELAY) {
        await sleep(1000)
      }

      try {
        const user = CacheAdapter.get('userData')

        Sentry.setUser({
          email: user?.attributes.email,
          username: user?.attributes.name,
        })
      } catch (e) {
        console.log('Error getting Amplify session:', e)
        // await Auth.signOut()
        Sentry.addBreadcrumb({
          category: 'logout',
          message: 'User logged out',
          level: 'info',
        })

        // window.location.replace(env.REACT_APP_HUB_ROUTE)
      }

      // Log request to Sentry
      Sentry.addBreadcrumb({
        category: 'B2B-http',
        message: `Request: ${config.method?.toUpperCase()} ${config.url}`,
        level: 'info',
        data: {
          url: config.url,
          method: config.method,
          headers: JSON.stringify(config.headers, null, 2),
          // data: JSON.stringify(config.data, null, 2),
        },
      })

      return config
    }

    const getErrorResponseHandler = async (
      error: IAxiosError,
    ): Promise<void> => {
      const axiosError = error as IAxiosError

      if (axiosError.response) {
        const { status, data } = axiosError.response

        let message = ''

        if (data.error) {
          message = data.error && data.error.message ? data.error.message : ''

          message +=
            data.error && data.error.detail ? ` - ${data.error.detail}` : ''
        } else {
          Sentry.setTag('errorUnknown', JSON.stringify(data))

          message = 'Aviso, não foi possível processar a solicitação'
        }

        if (status >= 400 && status < 500) {
          toast.warning(
            `${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
            {
              className: 'bg-warning text-white',
              dismissible: true,
              duration: 5000,
            },
          )
        } else if (status >= 500) {
          toast.error(
            `${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
            {
              className: 'bg-error text-white',
              dismissible: true,
              duration: 5000,
            },
          )
        } else {
          toast.info(`${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, {
            className: 'bg-info text-white',
            dismissible: true,
            duration: 5000,
          })
        }

        if (data && data.error) {
          if (data.error.message) {
            Sentry.setTag('errorMessage', data.error.message)
          } else {
            Sentry.setTag('errorWithNoMessage', JSON.stringify(data.error))
          }
        }

        // Log error to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: `Response Error: ${status}`,
          level: 'error',
          data: {
            url: axiosError.config.url,
            method: axiosError.config.method,
            status,
            error: JSON.stringify(data, null, 2),
          },
        })
      } else {
        // Log error to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: 'Network or unknown error',
          level: 'error',
          data: {
            error: JSON.stringify(axiosError, null, 2),
          },
        })
      }

      Sentry.captureException(axiosError)

      await Promise.reject(axiosError)
    }

    axiosInstance.interceptors.request.use(amplifyInterceptor)
    const interceptor = axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: `Response: ${response.status} ${response.config.url}`,
          level: 'info',
          data: {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            response: JSON.stringify(response, null, 2),
          },
        })

        return response
      },
      getErrorResponseHandler,
    )

    return () => {
      axiosInstance.interceptors.response.eject(interceptor)
    }
  }, [])

  return children
}

axiosInstance.interceptors.request.use(async (config) => {
  const session = await Auth.currentSession()
  const token = session.getIdToken().getJwtToken()

  if (config.headers) {
    config.headers.Authorization = token
  } else {
    config.headers = { Authorization: token }
  }

  return config
})

// axios.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     if (error instanceof AxiosError) {
//       if (error.response?.status === 401) {
//         window.location.href = env.REACT_APP_HUB_ROUTE
//       }
//     }

//     return Promise.reject(error)
//   },
// )

const get = async <T = any,>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  // Adiciona o echoToken como pathParam
  const echoToken = uuidv4()
  const updatedUrl = `${url}?echoToken=${echoToken}`

  const response: AxiosResponse<T> = await axiosInstance.get<T>(
    updatedUrl,
    config,
  )
  return response
}

const post = async (
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  // Adiciona o echoToken ao body da requisição
  const echoToken = uuidv4()
  const updatedData = { ...data, echoToken }

  const response: AxiosResponse = await axiosInstance.post(
    url,
    updatedData,
    config,
  )
  return response
}

const put = async <T = any,>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  // Adiciona o echoToken ao body da requisição
  const echoToken = uuidv4()
  const updatedData = { ...data, echoToken }

  const response: AxiosResponse<T> = await axiosInstance.put<T>(
    url,
    updatedData,
    config,
  )
  return response
}

const patch = async <T = any,>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const response: AxiosResponse<T> = await axiosInstance.patch<T>(
    url,
    data,
    config,
  )

  return response
}

const del = async <T = any,>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const response: AxiosResponse<T> = await axiosInstance.delete<T>(url, config)
  return response
}

export const httpClient = {
  get,
  post,
  put,
  patch,
  del,
  AxiosInterceptor,
}
